import type { Translations } from './types'

export const en: Translations = {
  header: {
    login: 'Sign in',
    logout: 'Sign out',
    myAccount: 'My account',
  },

  nav: {
    edit: 'Edit',
    myCVs: 'My CVs',
    preview: 'Preview',
    import: 'Import',
  },

  editor: {
    title: 'Edit CV',
    autoSaved: 'Auto-saved',
    saving: 'Saving...',
    saved: 'Saved',
    completeness: 'CV completeness',
    alreadyAutoSaved: 'Already auto-saved',
  },

  sections: {
    photo: 'CV Photo',
    personalInfo: 'Personal Information',
    links: 'Links',
    summary: 'Summary',
    experience: 'Experience',
    education: 'Education',
    skills: 'Skills',
    languages: 'Languages',
    settings: 'Settings',
  },

  personalInfoForm: {
    fullName: 'Full name',
    fullNamePlaceholder: 'Your full name',
    professionalTitle: 'Professional title',
    professionalTitlePlaceholder: 'e.g. Software Engineer',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    phone: 'Phone',
    phonePlaceholder: '+1 555 123 4567',
    location: 'Location',
    locationPlaceholder: 'City, Country',
    birthDate: 'Date of birth',
    birthDateHelper: 'Format: YYYY-MM-DD',
  },

  linksForm: {
    label: 'Label',
    labelPlaceholder: 'e.g. LinkedIn, GitHub, Portfolio',
    url: 'URL',
    urlPlaceholder: 'https://...',
    add: '+ Add',
    empty: 'Add links to LinkedIn, GitHub, portfolio, etc.',
    addFirst: '+ Add first link',
    noLinks: 'No links added',
    invalidUrl: 'Invalid URL',
  },

  summaryForm: {
    label: 'Professional summary',
    placeholder: 'Professional with X years of experience in... Specialized in... Looking for opportunities in...',
    hint: 'A brief paragraph highlighting your experience, skills, and professional goals.',
    improve: 'Improve with AI',
  },

  experienceForm: {
    company: 'Company',
    companyPlaceholder: 'Company name',
    position: 'Position',
    positionPlaceholder: 'Your job title',
    location: 'Location',
    locationPlaceholder: 'City, Country',
    startDate: 'Start date',
    endDate: 'End date',
    currentJob: 'Current job',
    description: 'Description',
    descriptionPlaceholder: 'Describe your main responsibilities and achievements...',
    delete: 'Delete experience',
    add: '+ Add experience',
    empty: 'Add your work experience, starting with the most recent.',
    itemTitle: 'Experience',
  },

  educationForm: {
    institution: 'Institution',
    institutionPlaceholder: 'University or school',
    degree: 'Degree',
    degreePlaceholder: 'Bachelor, Master, PhD...',
    field: 'Field of study',
    fieldPlaceholder: 'Computer Science, Business...',
    location: 'Location',
    locationPlaceholder: 'City, Country',
    startDate: 'Start date',
    endDate: 'End date',
    currentlyStudying: 'Currently studying',
    gpa: 'GPA (optional)',
    gpaPlaceholder: '3.8 / 4.0',
    delete: 'Delete education',
    add: '+ Add education',
    empty: 'Add your academic background, starting with the most recent.',
    itemTitle: 'Education',
  },

  skillsForm: {
    technical: 'Technical skills',
    technicalPlaceholder: 'e.g. React, TypeScript, Node.js',
    soft: 'Soft skills',
    softPlaceholder: 'e.g. Leadership, Communication',
    tools: 'Tools',
    toolsPlaceholder: 'e.g. VS Code, Figma, Docker',
    addHint: 'Press Enter to add',
    add: 'Add',
    removeAria: 'Remove',
  },

  languagesForm: {
    name: 'Language',
    namePlaceholder: 'e.g. English',
    level: 'Level',
    delete: 'Delete',
    add: '+ Add language',
    selectLevel: 'Select a level',
    empty: 'Add languages you speak and your proficiency level.',
    itemTitle: 'Language',
    levels: {
      native: 'Native',
      fluent: 'Fluent',
      advanced: 'Advanced',
      intermediate: 'Intermediate',
      basic: 'Basic',
    },
  },

  settingsForm: {
    dateFormat: 'Date format',
    dateFormatDesc: 'Choose how dates are displayed on your CV',
    monthYear: 'Month & Year',
    yearOnly: 'Year only',
    cvLanguage: 'CV language',
    cvLanguageDesc: 'Language for labels (Experience, Education, etc.)',
    contactIcons: 'Contact icons',
    contactIconsDesc: 'Show icons next to email, phone and location',
    cvLabelHint: 'Label on CV',
  },

  photoForm: {
    upload: 'Upload photo',
    dragDrop: 'Drag a photo or click to select',
    maxSize: 'JPG, PNG or WebP (max 8MB)',
    remove: 'Remove photo',
    photoLoaded: 'Photo uploaded',
    changeHint: 'Click X to change the photo',
    processing: 'Processing image...',
    dropHere: 'Drop the image here',
    validationError: 'Error validating image',
    processError: 'Error processing image',
    templateHint: 'The photo will appear on your CV according to the selected template. It is optional.',
  },

  previewPane: {
    title: 'Preview',
    subtitle: 'This is how your CV will look',
    template: 'Template',
    chooseTemplate: 'Choose a template',
    download: 'Download',
    share: 'Share',
  },

  cvList: {
    title: 'My CVs',
    newCV: '+ New CV',
    new: 'New',
    rename: 'Rename CV',
    delete: 'Delete CV',
    deleteConfirm: 'Delete this CV?',
    noTitle: 'Untitled',
    now: 'Now',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
    importBtn: 'Import PDF',
    import: 'Import',
    expand: 'Expand panel',
    collapse: 'Collapse panel',
    empty: 'No saved CVs',
    emptySubtitle: 'Create a new one or import a PDF',
    saved: 'CV saved',
    savedPlural: 'CVs saved',
    newCVDefault: 'My CV',
  },

  import: {
    title: 'Import CV from PDF',
    subtitle: 'Upload your existing CV and we will convert it to an editable format',
    dragDrop: 'Drag a PDF here or click to select',
    maxFileSize: 'Max 10MB',
    analyzing: 'Analyzing with AI...',
    review: 'Review extracted data',
    reviewTitle: 'Extracted data',
    importBtn: 'Import',
    cancel: 'Cancel',
    readingPdf: 'Reading PDF file...',
    extractingText: 'Extracting text from document...',
    analyzingAI: 'Analyzing with artificial intelligence...',
    finishing: 'Finishing...',
    completed: 'Completed',
    tipsTitle: 'Tips for better results',
    tipSelectable: 'Use PDFs with selectable text (not scanned)',
    tipStandard: 'Standard CV formats work best',
    tipEditable: 'You can edit the data after importing',
    errorTitle: 'Error processing PDF',
    importedTitle: 'Imported CV',
    importedSuccess: 'CV imported successfully',
    mayTakeSeconds: 'This may take a few seconds',
    stepReading: 'Reading PDF',
    stepExtracting: 'Extracting text',
    stepAnalyzing: 'Analyzing with AI',
    stepDone: 'Done',
    invalidFile: 'Invalid file',
    noTextExtracted: 'Could not extract text from the PDF. This may happen if the PDF is a scanned image or the text is protected. Try with a PDF that has selectable text.',
    invalidFileType: 'Invalid file',
    onlyPdf: 'Only PDF files are allowed',
    dropHere: 'Drop the file here',
    dropToImport: 'Drop to start importing',
    dragOrClick: 'Drag your CV here or click to select',
    tapToSelect: 'Tap to select your CV',
    pdfUpTo: 'PDF files up to 10MB',
    fileMustBePdf: 'File must be a PDF',
    fileTooLarge: 'File is too large. Maximum',
    fileMustBeAudio: 'File must be a valid audio file (MP3, WAV, WebM, M4A)',
    invalidEmail: 'Invalid email address',
    invalidUrl: 'Invalid URL',
    invalidPhone: 'Invalid phone number',
  },

  welcome: {
    title: 'Create your professional CV in minutes',
    subtitle: 'Choose how you want to start',
    createFromScratch: 'Start from scratch',
    scratchDesc: 'Start with a blank CV and fill it in step by step',
    importPdf: 'Import PDF',
    importDesc: 'Upload your existing CV and edit it with our tools',
    voiceCreate: 'Create with voice',
    voiceDesc: 'Dictate your experience and we will generate your CV automatically',
    voiceComingSoon: 'Coming soon',
  },

  aiReview: {
    reviewCV: 'Review CV',
    reviewing: 'Reviewing...',
    score: 'Score',
    strengths: 'Strengths',
    improvements: 'Areas for improvement',
    applySuggestion: 'Apply suggestion',
    applied: 'Applied',
    copied: 'Copied',
    loginToReview: 'Sign in to review your CV with AI',
    addContentFirst: 'Add content to your CV before reviewing',
    reviewWithAITitle: 'Review CV with AI',
    loginToReviewTitle: 'Sign in to review your CV',
    aiWillAnalyze: 'AI will analyze your CV and give you suggestions to improve it.',
    reviewNow: 'Review now',
    reviewTitle: 'CV Review',
    scoreLabel: 'Score',
    excellent: 'Excellent',
    good: 'Good',
    needsImprovement: 'Needs improvement',
    needsAttention: 'Needs attention',
    detailedRecommendations: 'Detailed recommendations',
    suggestion: 'Suggestion:',
    copiedPaste: 'Copied - paste it in the form',
    applyFix: 'Apply fix',
    close: 'Close',
    sectionPersonal: 'Personal Information',
    sectionSummary: 'Summary',
    sectionExperience: 'Experience',
    sectionEducation: 'Education',
    sectionSkills: 'Skills',
    sectionLanguages: 'Languages',
    sectionGeneral: 'General',
    loginToUseAI: 'You must sign in to use AI',
    noTextToImprove: 'No text to improve',
    suggestionError: 'Error getting suggestions',
    rateLimitReached: 'You have reached the review limit',
    reviewProcessError: 'Error processing the review. Try again.',
    unknownReviewError: 'Unknown error reviewing the CV',
  },

  aiSuggestion: {
    title: 'AI Suggestion',
    reviewImproved: 'Review the improved version of your text',
    original: 'Original',
    improved: 'Improved',
    additionalSuggestions: 'Additional suggestions',
    useImproved: 'Use improved text',
  },

  auth: {
    login: 'Sign in',
    register: 'Create account',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Repeat your password',
    fullName: 'Full name',
    fullNamePlaceholder: 'John Doe',
    forgotPassword: 'Forgot your password?',
    forgotPasswordTitle: 'Reset password',
    forgotPasswordSubtitle: 'We will send you an email with instructions to reset your password',
    emailSentTitle: 'Email sent',
    emailSentDescription: 'Check your inbox and follow the instructions to reset your password.',
    backToLogin: 'Back to sign in',
    sendEmail: 'Send email',
    user: 'User',
    orContinueWith: 'Or continue with',
    orContinueEmail: 'or continue with email',
    orRegisterEmail: 'or register with email',
    google: 'Google',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    resetPassword: 'Reset password',
    resetSent: 'Reset email sent',
    welcomeBack: 'Welcome back',
    loginSubtitle: 'Sign in to access your CVs',
    registerTitle: 'Create your account',
    registerSubtitle: 'Save your CVs in the cloud and access from any device',
    signUpFree: 'Sign up free',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    nameRequired: 'Name is required',
    passwordMinLength: 'Password must be at least 6 characters',
    passwordMismatch: 'Passwords do not match',
    signOut: 'Sign out',
  },

  shortcuts: {
    title: 'Keyboard shortcuts',
    subtitle: 'Navigate faster',
    undo: 'Undo',
    redo: 'Redo',
    save: 'Save (already auto-saved)',
    downloadPdf: 'Download PDF',
    showShortcuts: 'Show shortcuts',
  },

  common: {
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    copied: 'Copied to clipboard',
  },

  emptyState: {
    title: 'Your CV will appear here',
    subtitle: 'Start filling out the form',
  },

  cvListFull: {
    localCVTitle: 'You have a locally saved CV',
    anonymousTitle: 'You haven\'t imported any CV yet',
    noCVsTitle: 'No CVs yet',
    localCVDesc: 'It looks like you were working on a CV before creating your account. Want to import it?',
    anonymousDesc: 'Import your CV from a PDF or create a new one. Your CVs will be saved in this browser.',
    noCVsDesc: 'Create your first professional CV in minutes. It\'s free and easy to use.',
    importing: 'Importing...',
    importLocal: 'Import local CV',
    importPdf: 'Import PDF',
    createNew: 'Create new CV',
    importSuccess: 'CV imported successfully',
    importError: 'Error importing CV',
    localFound: 'Local CV found',
    localFoundDesc: 'You have a CV saved in this browser that is not synced',
    deleteConfirm: 'Are you sure you want to delete this CV?',
    defaultBadge: 'Default',
    edit: 'Edit',
    setDefault: 'Set as default',
    delete: 'Delete',
    expAbbr: 'exp.',
    eduAbbr: 'edu.',
    loadError: 'Error loading CVs',
    cvDeleted: 'CV deleted',
    cvSetDefault: 'CV set as default',
    deleteError: 'Error deleting CV',
    setDefaultError: 'Error setting default CV',
  },

  templates: {
    cleanDesc: 'Minimalist and professional',
    boldDesc: 'Modern and bold',
    creativeDesc: 'Colorful and visual',
    techDesc: 'Developer style',
    executiveDesc: 'Elegant and formal',
    modernDesc: 'Full-color header',
    colors: 'Colors:',
  },

  completeness: {
    title: 'CV completeness',
    fullName: 'Full name',
    professionalTitle: 'Professional title',
    contactInfo: 'Contact information',
    professionalSummary: 'Professional summary',
    workExperience: 'Work experience',
    education: 'Education',
    skills: 'Skills',
    languages: 'Languages',
  },

  notFound: {
    title: 'Page not found',
    goToEditor: 'Go to editor',
  },

  authErrors: {
    emailAlreadyInUse: 'This email is already registered',
    invalidEmail: 'Invalid email',
    operationNotAllowed: 'Operation not allowed',
    weakPassword: 'Password must be at least 6 characters',
    userDisabled: 'This account has been disabled',
    userNotFound: 'No account exists with this email',
    wrongPassword: 'Incorrect password',
    invalidCredential: 'Invalid credentials',
    tooManyRequests: 'Too many attempts. Try again later',
    popupClosed: 'Sign-in window was closed',
    authError: 'Authentication error',
    unknownError: 'Unknown error',
  },

  publicCV: {
    notExist: 'This CV does not exist or was unpublished',
    createFree: 'Create your CV for free',
    loadingCV: 'Loading CV...',
    downloadPdf: 'Download PDF',
    createdWith: 'Created with',
    createFreeMinutes: 'Create your CV for free in minutes',
  },

  importReview: {
    processedSuccess: 'PDF processed successfully',
    reviewExtracted: 'Review the extracted data before continuing',
    analyzedWithAI: 'Analyzed with AI',
    personalInfo: 'Personal Information',
    summary: 'Summary',
    experience: 'Experience',
    education: 'Education',
    languages: 'Languages',
    more: 'more',
    editableNote: 'You can edit all data after importing. Automatic extraction may not be perfect.',
    importAndEdit: 'Import and edit',
  },

  landing: {
    nav: {
      features: 'Features',
      templates: 'Templates',
      howItWorks: 'How it works',
      startFree: 'Start free',
    },
    hero: {
      title: 'Your next CV,',
      titleHighlight: 'in minutes.',
      subtitle: 'Build a professional resume with AI, multiple templates, and instant PDF download. No signup required.',
      cta: 'Create my CV free',
      ctaSecondary: 'See templates',
      badge: 'Start for free',
    },
    features: {
      title: 'Everything you need',
      subtitle: 'Professional tools to craft the perfect CV',
      ai: 'AI Assistant',
      aiDesc: 'Improve your summary and get smart suggestions for every section of your CV.',
      templates: 'Multiple professional templates',
      templatesDesc: 'From minimalist to creative. Each template is designed to stand out, and we keep adding more.',
      pdf: 'Instant PDF download',
      pdfDesc: 'Export your CV as a high-quality PDF, ready to send to any company.',
      free: 'Free to start',
      freeDesc: 'Create, edit, and download your CV at no cost. Some premium templates available for those who want more.',
      noSignup: 'No signup required',
      noSignupDesc: 'Start building your CV immediately. Sign up only if you want cloud storage.',
      cloud: 'Cloud storage',
      cloudDesc: 'Create an account to sync your CVs and access from any device.',
    },
    templatesSection: {
      title: 'Templates for every style',
      subtitle: 'Choose the one that best fits your professional profile',
      clean: 'Clean',
      cleanDesc: 'Minimalist and professional. Ideal for corporate roles.',
      bold: 'Bold',
      boldDesc: 'Modern with dark sidebar. Perfect to stand out.',
      creative: 'Creative',
      creativeDesc: 'Colorful and visual. Ideal for creative roles.',
      tech: 'Tech',
      techDesc: 'Developer style with monospace and terminal vibes.',
      executive: 'Executive',
      executiveDesc: 'Elegant and formal. For senior positions.',
      modern: 'Modern',
      modernDesc: 'Full-color header. Fresh and current.',
    },
    howItWorks: {
      title: 'Three steps, one professional CV',
      subtitle: 'Creating your resume is this simple',
      step1Title: 'Fill in your details',
      step1Desc: 'Complete the form with your info, experience, and skills. AI helps along the way.',
      step2Title: 'Choose your template',
      step2Desc: 'Pick from multiple professional designs and customize colors instantly.',
      step3Title: 'Download and share',
      step3Desc: 'Export as a high-quality PDF or share with a public link.',
    },
    finalCta: {
      title: 'Your professional CV awaits',
      subtitle: 'Join thousands who already built their CV with CVtoday.',
      cta: 'Start now — it\'s free',
    },
    footer: {
      tagline: 'Build your professional CV in minutes.',
      product: 'Product',
      builder: 'CV Builder',
      templates: 'Templates',
      aiReview: 'AI Review',
      resources: 'Resources',
      blog: 'Blog',
      guide: 'CV Guide',
      examples: 'Examples',
      legal: 'Legal',
      privacy: 'Privacy',
      terms: 'Terms',
      copyright: 'CVtoday. All rights reserved.',
    },
  },

  cvLimit: {
    title: 'Create your account to continue',
    description: 'CVtoday is and will always be free. We just need you to sign up so your CVs are saved safely and you never lose them.',
  },

  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: February 8, 2026',
    intro: 'At CVtoday we take your privacy seriously. This policy describes what data we collect, how we use it, and your rights over it.',
    dataWeCollectTitle: 'Data we collect',
    dataWeCollectLocal: 'CV data stored locally: Your CV is saved in your browser\'s local storage (localStorage). This data never leaves your device unless you choose to create an account, publish your CV, or use AI features (import or review).',
    dataWeCollectAuth: 'Authentication data: If you create an account, we collect your name, email address, and authentication method (email/password or Google OAuth).',
    dataWeCollectFirestore: 'Cloud data: If you sign in, your CVs are synced with Firebase Firestore so you can access them from any device. Published CVs are stored publicly under the slug you choose.',
    howWeUseTitle: 'How we use your data',
    howWeUsePdf: 'PDF generation: Your CV data is processed in your browser to generate PDF documents. No data is sent to our servers for this process.',
    howWeUseAi: 'AI review: If you use the AI review feature, your CV content is sent to the Anthropic Claude API for analysis. Anthropic does not use this data for training.',
    howWeUseImport: 'PDF import: When you import a CV from a PDF, the extracted text is sent to our server (Cloud Function) which processes it with the Anthropic Claude API to extract structured data. The extraction result is stored in our database (Firestore) for auditing and service improvement purposes.',
    howWeUseSync: 'Cloud sync: If you have an account, your CVs are synced with Firebase for backup and multi-device access.',
    thirdPartyTitle: 'Third-party services',
    thirdPartyFirebase: 'Firebase (Google): User authentication, cloud storage (Firestore), and hosting. Subject to Google\'s privacy policy.',
    thirdPartyClaude: 'Anthropic Claude API: Used for AI CV review and PDF data extraction. Data is not used for model training.',
    thirdPartyKofi: 'Ko-fi: If you choose to support us, you will be redirected to Ko-fi to process the donation. CVtoday does not collect payment information.',
    cookiesTitle: 'Cookies & local storage',
    cookiesDesc: 'CVtoday does not use tracking or advertising cookies. We use browser localStorage to save your CV data and preferences (such as language). Firebase may set technical cookies necessary for authentication.',
    retentionTitle: 'Data retention & deletion',
    retentionDesc: 'Local data can be deleted by clearing your browser data. Cloud data can be deleted from your account or by contacting us at info@cv-today.com. Published CVs can be unpublished at any time from the editor.',
    contactTitle: 'Contact',
    contactDesc: 'If you have questions about this privacy policy, contact us at info@cv-today.com.',
  },

  terms: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: February 8, 2026',
    acceptanceTitle: 'Acceptance of terms',
    acceptanceDesc: 'By accessing and using CVtoday (cv-today.com), you agree to these terms of service. If you do not agree with any of these terms, do not use the service.',
    serviceTitle: 'Description of service',
    serviceDesc: 'CVtoday is a web application that allows you to create, edit, and download resumes (CVs) in PDF format. The service includes real-time editing, multiple templates, AI review, and online CV publishing.',
    accountsTitle: 'User accounts',
    accountsDesc: 'You can use CVtoday without creating an account. Basic features (creating and downloading CVs) are available without registration. Creating an account allows you to sync your CVs to the cloud, publish CVs online, and access additional features. You are responsible for maintaining the security of your account.',
    contentTitle: 'Content ownership',
    contentDesc: 'All content you create on CVtoday (CV data, text, photos) belongs to you. CVtoday does not claim any ownership rights over your content. By publishing a CV, you grant CVtoday permission to display it publicly at the URL you choose.',
    acceptableUseTitle: 'Acceptable use',
    acceptableUseDesc: 'You agree to use CVtoday only for lawful and legitimate purposes. You must not: attempt unauthorized access to other users or systems, use the service to distribute illegal or harmful content, attempt to interfere with the operation of the service, or use automated tools to access the service in bulk.',
    liabilityTitle: 'Limitation of liability',
    liabilityDesc: 'CVtoday is provided "as is" without warranties of any kind. We are not responsible for direct, indirect, or consequential damages arising from the use of the service. We do not guarantee that the service will be available uninterrupted or error-free. AI reviews are suggestions and do not constitute professional advice.',
    changesTitle: 'Changes to terms',
    changesDesc: 'We reserve the right to modify these terms at any time. Changes will be posted on this page with the updated date. Continued use of the service after changes constitutes your acceptance of the new terms.',
    contactTitle: 'Contact',
    contactDesc: 'If you have questions about these terms of service, contact us at info@cv-today.com.',
  },

  downloadModal: {
    preparing: 'Preparing your CV...',
    ready: 'Your CV is ready!',
    supportTitle: 'CVtoday is mostly free',
    supportMessage: 'If you found it useful, consider supporting us with a coffee. Every contribution helps us keep improving.',
    supportButton: 'Buy us a coffee',
  },

  publish: {
    sharePublicLink: 'Share your CV with a public link',
    cvUrl: 'Your CV URL',
    checking: 'Checking availability...',
    available: 'Available',
    unavailable: 'Not available, choose another',
    copyLink: 'Copy link',
    views: 'views',
    publishCV: 'Publish CV',
    update: 'Update',
    unpublish: 'Unpublish',
    registerToPublish: 'Sign up to publish',
    needAccountToPublish: 'You need an account to publish your CV',
    published: 'CV published',
    updated: 'CV updated',
    unpublished: 'CV unpublished',
    shareMessage: 'Check out my CV:',
    loginToPublish: 'You must sign in to publish',
    publishError: 'Error publishing',
    updateError: 'Error updating',
    unpublishError: 'Error unpublishing',
  },
}
